import { FunctionComponent } from "react";

import {
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { ServerMigrationResponse } from "@libs/api/gateways/field/field-ops-gateway.dtos";

import { ServerMigrationDeviceCard } from "./ServerMigrationDeviceCard";
import { ServerMigrationLabeledText } from "./ServerMigrationLabeledText";
import { ServerMigrationTenantSummary } from "./ServerMigrationTenantSummary";

interface ServerMigrationOverviewProps {
  response: ServerMigrationResponse;
}

const getSiteIdsBadge = (
  response: ServerMigrationResponse
): { text: string; color: TextBadgeColor } => {
  if (response.hasMatchingSiteIds)
    return { text: "Match", color: TextBadgeColor.green };

  const sourceSiteId = response.sourceFieldDevice?.siteId;
  const destinationSiteId = response.destinationFieldDevice?.siteId;
  if (
    sourceSiteId !== undefined &&
    destinationSiteId !== undefined &&
    sourceSiteId !== destinationSiteId
  )
    return { text: "Do not match", color: TextBadgeColor.red };

  // hasMatchingSiteIds defaults to false when validation fails before the
  // site id comparison runs, so it is not a real mismatch.
  if (!response.success)
    return { text: "Not checked", color: TextBadgeColor.lightGrey };

  return { text: "Do not match", color: TextBadgeColor.red };
};

export const ServerMigrationOverview: FunctionComponent<ServerMigrationOverviewProps> = ({
  response
}) => {
  const theme = useTheme();
  const siteIdsBadge = getSiteIdsBadge(response);

  return (
    <Stack tokens={{ childrenGap: theme.spacing.m }}>
      <Stack horizontal tokens={{ childrenGap: theme.spacing.m }}>
        <ServerMigrationDeviceCard
          title="Current server"
          device={response.sourceFieldDevice}
        />
        <ServerMigrationDeviceCard
          title="New server"
          device={response.destinationFieldDevice}
        />
      </Stack>
      <Stack tokens={{ childrenGap: theme.spacing.m }}>
        <ServerMigrationLabeledText label="Tenant" labelWidth={150}>
          <ServerMigrationTenantSummary
            tenant={response.sourceTenant}
            outcome="Will be managed by the new server"
          />
        </ServerMigrationLabeledText>
        <ServerMigrationLabeledText
          label="New server's tenant"
          labelWidth={150}
        >
          <ServerMigrationTenantSummary
            tenant={response.destinationFieldDeviceOriginalTenant}
            outcome="Will be deactivated"
          />
        </ServerMigrationLabeledText>
        <ServerMigrationLabeledText label="Site ids" labelWidth={150}>
          <TextBadge
            badgeSize={TextBadgeSize.small}
            badgeColor={siteIdsBadge.color}
            styles={{ root: { width: "fit-content" } }}
          >
            {siteIdsBadge.text}
          </TextBadge>
        </ServerMigrationLabeledText>
      </Stack>
    </Stack>
  );
};
